import React from "react";
import { Link } from "react-router-dom";
import groupBy from "lodash/groupBy";
import maxBy from "lodash/maxBy";
import meanBy from "lodash/meanBy";
import sortBy from "lodash/sortBy";
import sumBy from "lodash/sumBy";
import toPairs from "lodash/toPairs";
import moment, { Moment } from "moment-timezone";
import Color from "color";

import { formatBytes } from "utils/format";
import PanelSection from "ui/PanelSection";
import SQL from "ui/SQL";
import TimeBucketBar from "features/Logs/TimeBucketBar";
import { useRoutes } from "utils/routes";

import { logAnalysisBucketMinutes } from "./buckets";

import type {
  CheckpointStartingViewServerFragment,
  TempFileCreatedViewServerFragment,
} from "./gql/LogAnalysisView.v2.generated";

// The Log Analysis views for specific classifications. Each view renders its
// own Server fragment (series + related settings), mapping the v2 series (ISO
// bucketStart) to the epoch-second buckets TimeBucketBar is drawn from.

type SettingsByName = Record<string, string | null | undefined>;

// ---- Checkpoint Starting (W40) ----------------------------------------------------------------

type ReasonBucket = {
  timebucket: number;
  classification: string;
  count: number;
};

const REASON_DETAILS = {
  time: { title: "time", color: Color("#8FBC8F") },
  xlog: { title: "xlog", color: Color("#FF7F50") },
  unknown: { title: "Unknown", color: Color("#ccc") },
};

export function CheckpointStartingView({
  server,
  serverId,
  startTs,
  endTs,
  highlightedTime,
}: {
  server: CheckpointStartingViewServerFragment;
  serverId: string;
  startTs: number;
  endTs: number;
  highlightedTime: moment.Moment | null | undefined;
}) {
  const { serverConfigSetting } = useRoutes();
  const reasons: ReasonBucket[] = server.logAnalysis.checkpointStarting.map(
    (row) => ({
      timebucket: moment(row.bucketStart).unix(),
      classification: row.reason ?? "unknown",
      count: row.count,
    }),
  );
  const settings = settingsByName(server.postgresConfig.settings);

  const reasonKeys = Object.keys(REASON_DETAILS);
  let byReason = toPairs(groupBy(reasons, (l) => l.classification));
  byReason = sortBy(byReason, ([c]): number => {
    const idx = reasonKeys.indexOf(c);
    return idx === -1 ? Number.MAX_VALUE : idx;
  });

  const checkpointSegments = settings["checkpoint_segments"];

  return (
    <PanelSection>
      <table className="w-full">
        <tbody>
          {byReason.map(([classification, stats]) => (
            <ReasonLogStats
              key={classification}
              {...{ stats, classification, endTs, startTs, highlightedTime }}
            />
          ))}
          <tr>
            <td className="pt-2">
              <strong>Related Config Parameters:</strong>
            </td>
          </tr>
          <tr>
            <td className="font-mono">
              <Link
                to={serverConfigSetting(
                  serverId,
                  "checkpoint_completion_target",
                )}
              >
                checkpoint_completion_target
              </Link>{" "}
              = {settings["checkpoint_completion_target"] ?? "n/a"}
            </td>
          </tr>
          <tr>
            <td className="font-mono">
              <Link to={serverConfigSetting(serverId, "checkpoint_timeout")}>
                checkpoint_timeout
              </Link>{" "}
              = {settings["checkpoint_timeout"] ?? "n/a"}
            </td>
          </tr>
          {settings["max_wal_size"] && (
            <tr>
              <td className="font-mono">
                <Link to={serverConfigSetting(serverId, "max_wal_size")}>
                  max_wal_size
                </Link>{" "}
                = {settings["max_wal_size"]}
              </td>
            </tr>
          )}
          {checkpointSegments && (
            <tr>
              <td className="font-mono">
                <Link to={serverConfigSetting(serverId, "checkpoint_segments")}>
                  checkpoint_segments
                </Link>{" "}
                = {checkpointSegments} (
                {formatBytes(
                  parseInt(checkpointSegments, 10) * 16 * 1024 * 1024,
                )}
                )
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </PanelSection>
  );
}

function ReasonLogStats({
  stats,
  classification,
  startTs,
  endTs,
  highlightedTime,
}: {
  stats: ReasonBucket[];
  classification: string;
  startTs: number;
  endTs: number;
  highlightedTime: moment.Moment;
}) {
  const maxCount = maxBy(stats, (g) => g.count).count;
  const totalCount = sumBy(stats, (g) => g.count);
  const details = REASON_DETAILS[classification as keyof typeof REASON_DETAILS];

  const buckets = stats
    .filter((group) => group.timebucket >= startTs)
    .map((group) => ({
      start: moment.unix(group.timebucket),
      value: group.count,
    }));
  function TimeBucketTip({
    start,
    end,
    value,
  }: {
    start: Moment;
    end: Moment;
    value: number;
  }) {
    return (
      <div>
        <strong>{value}</strong> &times; {details?.title ?? classification}
        <div className="text-[var(--color-gray-700)]">
          {start.format("MMM DD hh:mma")}
          &mdash;
          {end.format("hh:mma zz")}
        </div>
      </div>
    );
  }
  const from = moment.unix(startTs);
  const to = moment.unix(endTs);
  const bucketSize = moment.duration(
    logAnalysisBucketMinutes(from, to),
    "minutes",
  );
  return (
    <>
      <tr>
        <td className="font-medium leading-[18px] pt-[5px]">
          {totalCount} &times; {details?.title ?? classification}
        </td>
      </tr>
      <tr>
        <td className="h-5">
          <TimeBucketBar
            buckets={buckets}
            bucketSize={bucketSize}
            maxValue={maxCount}
            color={details?.color}
            from={from}
            to={to}
            highlight={highlightedTime}
            tip={TimeBucketTip}
          />
        </td>
      </tr>
    </>
  );
}

// ---- Temp File Created (S7) -------------------------------------------------------------------

type TempFileBucket = {
  timebucket: number;
  count: number;
  avgSize: number;
  query: { id: string; databaseId: string; displaySql: string } | null;
};

export function TempFileCreatedView({
  server,
  serverId,
  startTs,
  endTs,
  highlightedTime,
}: {
  server: TempFileCreatedViewServerFragment;
  serverId: string;
  startTs: number;
  endTs: number;
  highlightedTime: moment.Moment | null | undefined;
}) {
  const { serverConfigSetting } = useRoutes();
  const buckets: TempFileBucket[] = server.logAnalysis.tempFileCreated.map(
    (row) => ({
      timebucket: moment(row.bucketStart).unix(),
      count: row.count,
      avgSize: row.avgSizeBytes ?? 0,
      query: row.query,
    }),
  );
  const settings = settingsByName(server.postgresConfig.settings);

  let byQuery = toPairs(groupBy(buckets, (l) => l.query?.id ?? "0"));
  byQuery = sortBy(
    byQuery,
    ([, stats]): number => -sumBy(stats, (g) => g.count),
  );

  return (
    <PanelSection>
      <table className="w-full">
        <tbody>
          {byQuery.map(([queryId, stats]) => (
            <QueryLogStats
              key={queryId}
              {...{ stats, queryId, endTs, startTs, highlightedTime }}
            />
          ))}
          <tr>
            <td className="pt-2">
              <strong>Related Config Parameters:</strong>
            </td>
          </tr>
          <tr>
            <td className="font-mono">
              <Link to={serverConfigSetting(serverId, "work_mem")}>
                work_mem
              </Link>{" "}
              = {settings["work_mem"] ?? "n/a"}
            </td>
          </tr>
        </tbody>
      </table>
    </PanelSection>
  );
}

function QueryLogStats({
  stats,
  queryId,
  startTs,
  endTs,
  highlightedTime,
}: {
  stats: TempFileBucket[];
  queryId: string;
  startTs: number;
  endTs: number;
  highlightedTime: moment.Moment;
}) {
  const { databaseQuery } = useRoutes();
  const perBucket = combineByBucket(stats);
  const maxCount = maxBy(perBucket, (g) => g.count).count;
  const totalCount = sumBy(perBucket, (g) => g.count);
  const avgSize = meanBy(perBucket, (g) => g.avgSize);
  const query = stats[0].query;

  const buckets = perBucket
    .filter((group) => group.timebucket >= startTs)
    .map((group) => ({
      start: moment.unix(group.timebucket),
      value: group.count,
    }));
  function TimeBucketTip({
    start,
    end,
    value,
  }: {
    start: Moment;
    end: Moment;
    value: number;
  }) {
    return (
      <div className="min-w-[200px]">
        <strong>{value}</strong> &times;{" "}
        {query ? <span>Query #{query.id}</span> : "Unknown Query"}
        <div className="text-[var(--color-gray-700)]">
          {start.format("MMM DD hh:mma")}
          &mdash;
          {end.format("hh:mma zz")}
        </div>
      </div>
    );
  }

  const from = moment.unix(startTs);
  const to = moment.unix(endTs);
  const bucketSize = moment.duration(
    logAnalysisBucketMinutes(from, to),
    "minutes",
  );
  return (
    <>
      <tr key={queryId + "_title"}>
        <td className="font-medium leading-[18px] pt-[5px]">
          {totalCount} &times;{" "}
          {query ? (
            <Link to={databaseQuery(query.databaseId, query.id)}>
              Query #{query.id}
            </Link>
          ) : (
            "Unknown Query"
          )}
        </td>
      </tr>
      <tr key={queryId}>
        <td>
          <div className="h-5">
            <TimeBucketBar
              buckets={buckets}
              bucketSize={bucketSize}
              maxValue={maxCount}
              color={Color("#FF7F50")}
              from={from}
              to={to}
              highlight={highlightedTime}
              tip={TimeBucketTip}
            />
          </div>
          <div className="mb-[5px]">
            <strong className="font-medium">Avg. Temp File Size</strong>:{" "}
            {formatBytes(avgSize)},{" "}
            <strong className="font-medium">Query:</strong>{" "}
            {query ? <SQL sql={query.displaySql} inline /> : "Unknown"}
          </div>
        </td>
      </tr>
    </>
  );
}

// Unresolved queries all share the "Unknown Query" row, so it can have several
// rows per time bucket: combine them, weighting average sizes by count.
function combineByBucket(stats: TempFileBucket[]): TempFileBucket[] {
  return Object.values(groupBy(stats, (g) => g.timebucket)).map((rows) => {
    const count = sumBy(rows, (r) => r.count);
    return {
      timebucket: rows[0].timebucket,
      count,
      avgSize: sumBy(rows, (r) => r.avgSize * r.count) / count,
      query: rows[0].query,
    };
  });
}

function settingsByName(
  settings: { name: string; value?: string | null }[],
): SettingsByName {
  return Object.fromEntries(settings.map((s) => [s.name, s.value]));
}
